import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCheck } from '@ng-icons/heroicons/outline';
import type { RevenueProduct } from '../../../core/api/generated/models/RevenueProduct';
import { PRODUCT_TYPE_ICONS, productTypeIcon, productTypeLabel } from './product-types.catalog';
import { formatProductPrice } from './product-billing.utils';

/**
 * Presentational, image-forward product card (estilo ecommerce): capa grande no
 * topo (cover), nome + preço compactos, campos vazios ocultos. Sem lógica de
 * clique — o container decide (grade: ações no rodapé; picker: card clicável).
 * Slots: [card-top] (canto sup-esq, ex. checkbox), [card-badges] (canto sup-dir),
 * [card-actions] (rodapé). Reusado na grade de Produtos e no picker de Vendas.
 */
@Component({
  selector: 'app-product-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon],
  providers: [provideIcons({ heroCheck, ...PRODUCT_TYPE_ICONS })],
  host: {
    '[attr.role]': "interactive() ? 'button' : null",
    '[attr.tabindex]': "interactive() && !disabled() ? '0' : null",
    '[attr.aria-pressed]': 'interactive() ? selected() : null',
    '[attr.aria-disabled]': 'interactive() && disabled() ? true : null',
    '[class.pc-host--interactive]': 'interactive()',
    '(click)': 'onPress()',
    '(keydown.enter)': 'onPress()',
    '(keydown.space)': 'onPressKey($event)',
  },
  template: `
    <article class="pc" [class.pc--selected]="selected()" [class.pc--disabled]="disabled()">
      <div class="pc__media">
        @if (coverUrl()) {
          <img [src]="coverUrl()" alt="" loading="lazy" class="pc__img" />
        } @else {
          <ng-icon [name]="typeIcon()" size="28" class="pc__placeholder" aria-hidden="true" />
        }

        <div class="pc__top"><ng-content select="[card-top]" /></div>
        <div class="pc__badges"><ng-content select="[card-badges]" /></div>

        @if (selected()) {
          <span class="pc__check" aria-hidden="true"><ng-icon name="heroCheck" size="16" /></span>
        }
      </div>

      <div class="pc__body">
        <h3 class="pc__name">{{ product().name }}</h3>
        <p class="pc__type">{{ typeLabel() }}</p>
        <p class="pc__price">{{ priceLabel() }}</p>
      </div>

      <ng-content select="[card-actions]" />
    </article>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }

      .pc {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        border: 1px solid var(--color-line);
        border-radius: var(--radius-lg, var(--radius-md));
        background: var(--color-panel);
        transition: border-color 120ms ease, box-shadow 120ms ease;
      }

      .pc--selected {
        border-color: var(--color-accent);
        box-shadow: 0 0 0 1px var(--color-accent);
      }

      .pc--disabled {
        opacity: 0.55;
      }

      :host(.pc-host--interactive) {
        display: block;
        cursor: pointer;
        outline: none;
      }

      :host(.pc-host--interactive) .pc {
        transition: border-color 120ms ease, box-shadow 120ms ease;
      }

      :host(.pc-host--interactive:hover) .pc {
        border-color: color-mix(in oklch, var(--color-accent) 45%, var(--color-line));
      }

      :host(.pc-host--interactive:focus-visible) .pc {
        border-color: var(--color-accent);
        box-shadow: 0 0 0 2px color-mix(in oklch, var(--color-accent) 35%, transparent);
      }

      :host(.pc-host--interactive[aria-disabled='true']) {
        cursor: not-allowed;
      }

      .pc__media {
        position: relative;
        aspect-ratio: 1 / 1;
        display: grid;
        place-items: center;
        padding: 0.75rem;
        background: var(--color-soft);
        border-bottom: 1px solid var(--color-line);
        color: var(--color-muted);
      }

      /* Imagem inteira dentro da moldura fixa (não corta; dá zoom p/ caber). */
      .pc__img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      .pc__top {
        position: absolute;
        top: 0.5rem;
        left: 0.5rem;
      }

      .pc__badges {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.25rem;
      }

      .pc__check {
        position: absolute;
        bottom: 0.5rem;
        right: 0.5rem;
        display: grid;
        place-items: center;
        width: 1.6rem;
        height: 1.6rem;
        border-radius: var(--radius-pill);
        background: var(--color-accent);
        color: var(--color-panel);
      }

      .pc__body {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
        padding: 0.75rem;
      }

      .pc__name {
        margin: 0;
        font-weight: 700;
        font-size: 0.95rem;
        color: var(--color-ink);
        line-height: 1.25;
        /* Altura fixa de 2 linhas: nome curto ou longo ocupam o mesmo espaço. */
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 2.4rem;
      }

      .pc__type {
        margin: 0;
        font-size: 0.75rem;
        color: var(--color-muted);
      }

      .pc__price {
        margin: 0.15rem 0 0;
        font-family: var(--font-brand);
        font-size: 0.95rem;
        font-weight: 720;
        color: var(--color-accent-strong);
      }
    `,
  ],
})
export class ProductCardComponent {
  readonly product = input.required<RevenueProduct>();
  readonly selected = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  /** When true the whole card is a clickable control (role=button) and emits `pressed`. */
  readonly interactive = input(false, { transform: booleanAttribute });

  readonly pressed = output<void>();

  onPress(): void {
    if (this.interactive() && !this.disabled()) this.pressed.emit();
  }
  onPressKey(event: Event): void {
    event.preventDefault();
    this.onPress();
  }

  readonly coverUrl = computed(() => this.product().cover_image?.url ?? null);
  readonly typeIcon = computed(() => productTypeIcon(this.product().type));
  // Sempre renderiza tipo e preço (área de texto de altura fixa); vazio vira 'Sem tipo' / '—'.
  readonly typeLabel = computed(() => productTypeLabel(this.product().type));
  readonly priceLabel = computed(() => {
    const p = this.product();
    return p.amount != null && p.currency != null ? formatProductPrice(p) : '—';
  });
}
